import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { API_BASE_URL } from '../../../config/api';

// Mapiranje kodova zemalja za Evropu
const COUNTRY_CODES_MAP = {
  'rs': 'rs', // Srbija
  'ba': 'ba', // Bosna i Hercegovina
  'hr': 'hr', // Hrvatska
  'me': 'me', // Crna Gora
  'mk': 'mk', // Makedonija
  'si': 'si'  // Slovenija
};

const StreetAutocomplete = ({ value, onChange, inputClass, placeholder }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  
  // Dobij grad iz kompanije
  const getCompanyCity = () => {
    try {
      if (user?.result?.city) {
        return user.result.city;
      }
      
      if (typeof window !== 'undefined') {
        const profileStr = localStorage.getItem('profile');
        if (profileStr) {
          const profile = JSON.parse(profileStr);
          if (profile?.result?.city) {
            return profile.result.city;
          }
        }
      }
    } catch (e) {
      console.error('Error reading city from store:', e);
    }
    return null;
  };

  const getCountryCode = () => {
    try {
      if (user?.result?.countryCode) {
        return COUNTRY_CODES_MAP[user.result.countryCode.toLowerCase()] || 'rs';
      }
      
      if (typeof window !== 'undefined') {
        const profileStr = localStorage.getItem('profile');
        if (profileStr) {
          const profile = JSON.parse(profileStr);
          if (profile?.result?.countryCode) {
            return COUNTRY_CODES_MAP[profile.result.countryCode.toLowerCase()] || 'rs';
          }
        }
      }
    } catch (e) {
      console.error('Error reading country code:', e);
    }
    return 'rs';
  };

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const lastSearchQueryRef = useRef('');
  const abortControllerRef = useRef(null);

  const city = getCompanyCity();
  const countryCode = getCountryCode();

  // Search function sa Photon API (brz)
  const searchStreets = useCallback(async (query) => {
    if (!query || query.length < 3) { // Minimum 3 karaktera za brže rezultate
      setSuggestions([]);
      setShowSuggestions(false);
      lastSearchQueryRef.current = '';
      return;
    }

    // Ako je query isti kao poslednji, preskoči
    if (lastSearchQueryRef.current === query && suggestions.length > 0) {
      return;
    }

    // Otkaži prethodni zahtev ako postoji
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    lastSearchQueryRef.current = query;
    setIsLoading(true);
    
    // Kreiraj novi AbortController
    abortControllerRef.current = new AbortController();
    
    try {
      const params = new URLSearchParams({
        q: query,
        countryCode: countryCode
      });
      
      if (city) {
        params.append('city', city);
      }

      const apiUrl = `${API_BASE_URL}/api/geocoding/street-suggestions?${params.toString()}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch suggestions: ${response.status}`);
      }

      const data = await response.json();
      
      if (!Array.isArray(data)) {
        setSuggestions([]);
        setShowSuggestions(false);
        setIsLoading(false);
        return;
      }
      
      if (data.length === 0) {
        setSuggestions([]);
        setShowSuggestions(false);
        setIsLoading(false);
        return;
      }

      setSuggestions(data);
      setShowSuggestions(data.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      if (error.name === 'AbortError') {
        // Zahtev je otkazan, ignoriši grešku
        return;
      }
      console.error('Error fetching street suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [city, countryCode, suggestions.length]);

  // Handle input change sa debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    
    if (onChange) {
      onChange({ street: newValue });
    }

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Ako je vrednost prazna, resetuj sugestije odmah
    if (!newValue || newValue.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      lastSearchQueryRef.current = '';
      return;
    }

    // Debounce search - 300ms je dovoljno za brz API
    debounceTimerRef.current = setTimeout(() => {
      if (inputRef.current && inputRef.current.value === newValue) {
        searchStreets(newValue);
      }
      debounceTimerRef.current = null;
    }, 300);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    if (onChange) {
      onChange({ street: suggestion.name });
    }
    
    lastSearchQueryRef.current = suggestion.name;
    
    setShowSuggestions(false);
    setSuggestions([]);
    
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'ArrowDown' && value && value.length >= 3) {
        searchStreets(value);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Scroll selected suggestion into view
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionsRef.current) {
      const selectedElement = suggestionsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value || ''}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (value && value.length >= 3 && suggestions.length === 0) {
            searchStreets(value);
          } else if (suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        className={inputClass}
        autoCapitalize="words"
        autoCorrect="off"
        spellCheck={false}
        placeholder={placeholder || t('jobs.enterStreetName')}
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-[9999] w-full mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
          style={{ zIndex: 9999 }}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              onClick={() => handleSuggestionSelect(suggestion)}
              className={`px-3 py-2 cursor-pointer border-b border-gray-700 last:border-b-0 ${
                index === selectedIndex
                  ? 'bg-gray-700'
                  : 'hover:bg-gray-700'
              }`}
            >
              <div className="text-sm text-white font-medium">
                {suggestion.name}
              </div>
              {suggestion.fullAddress && suggestion.fullAddress !== suggestion.name && (
                <div className="text-xs text-gray-400 mt-0.5">
                  {suggestion.fullAddress}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {isLoading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
        </div>
      )}
    </div>
  );
};

export default StreetAutocomplete;

